import React, { useState } from "react";

const COUNTRY_CODE_DEFAULT = "52";

interface PhoneNumberStepProps {
  onPhoneNumberChange: (value: string) => void;
  onMatchChange: (matches: boolean) => void;
  refundTotal: number;
}

export const PhoneNumberStep: React.FC<PhoneNumberStepProps> = ({
  onPhoneNumberChange,
  onMatchChange,
  refundTotal,
}) => {
  const [countryCode, setCountryCode] = useState(COUNTRY_CODE_DEFAULT);
  const [localNumber, setLocalNumber] = useState("");
  const [confirmCountryCode, setConfirmCountryCode] = useState(COUNTRY_CODE_DEFAULT);
  const [confirmLocalNumber, setConfirmLocalNumber] = useState("");

  const fullPhone = `+${countryCode}${localNumber}`;
  const confirmFullPhone = `+${confirmCountryCode}${confirmLocalNumber}`;

  const localDigits = localNumber.replace(/\D/g, "");
  const isLocalValid = countryCode.length > 0 && localDigits.length === 10;
  const matches = isLocalValid && fullPhone === confirmFullPhone;

  const notify = (phone: string, cc: string, local: string, ccc: string, cLocal: string) => {
    const full = `+${cc}${local}`;
    const confirmFull = `+${ccc}${cLocal}`;
    const digits = local.replace(/\D/g, "");
    const valid = cc.length > 0 && digits.length === 10;
    onPhoneNumberChange(full);
    onMatchChange(valid && full === confirmFull);
  };

  const handleCountryCode = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 3);
    setCountryCode(digits);
    notify(`+${digits}${localNumber}`, digits, localNumber, confirmCountryCode, confirmLocalNumber);
  };

  const handleLocalNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    setLocalNumber(digits);
    notify(`+${countryCode}${digits}`, countryCode, digits, confirmCountryCode, confirmLocalNumber);
  };

  const handleConfirmCountryCode = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 3);
    setConfirmCountryCode(digits);
    notify(fullPhone, countryCode, localNumber, digits, confirmLocalNumber);
  };

  const handleConfirmLocalNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    setConfirmLocalNumber(digits);
    notify(fullPhone, countryCode, localNumber, confirmCountryCode, digits);
  };

  const showMismatch =
    isLocalValid &&
    confirmLocalNumber.length > 0 &&
    !matches;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Se enviará un mensaje de WhatsApp al cliente solicitando su CLABE
        interbancaria para procesar el reembolso de{" "}
        <span className="font-bold text-red-600">${refundTotal.toFixed(2)}</span>{" "}
        por tarjeta.
      </p>
      <div className="space-y-3">
        <div className="space-y-1">
          <label htmlFor="phone-local" className="text-sm font-medium text-gray-700">
            Número de WhatsApp del cliente
          </label>
          <div className="flex items-center rounded-md ring-1 ring-gray-300 focus-within:ring-blue-500">
            <span className="flex items-center rounded-l-md border-r border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-500 select-none">
              +
            </span>
            <input
              type="tel"
              autoComplete="off"
              className="w-14 border-r border-gray-200 px-2 py-2 text-sm text-center focus:outline-none"
              placeholder="52"
              value={countryCode}
              onChange={(e) => handleCountryCode(e.target.value)}
              onPaste={(e) => e.preventDefault()}
            />
            <input
              id="phone-local"
              type="tel"
              autoComplete="off"
              className="flex-1 rounded-r-md px-3 py-2 text-sm focus:outline-none"
              placeholder="1234567890"
              value={localNumber}
              onChange={(e) => handleLocalNumber(e.target.value)}
              onPaste={(e) => e.preventDefault()}
            />
          </div>
          <p className="text-xs text-gray-400">
            Código de país + 10 dígitos locales
          </p>
        </div>
        <div className="space-y-1">
          <label htmlFor="phone-confirm-local" className="text-sm font-medium text-gray-700">
            Confirmar número de WhatsApp
          </label>
          <div className={`flex items-center rounded-md ring-1 ${showMismatch ? "ring-red-400 focus-within:ring-red-500" : "ring-gray-300 focus-within:ring-blue-500"}`}>
            <span className={`flex items-center rounded-l-md border-r ${showMismatch ? "border-red-200" : "border-gray-200"} bg-gray-50 px-2 py-2 text-sm text-gray-500 select-none`}>
              +
            </span>
            <input
              type="tel"
              autoComplete="off"
              className={`w-14 border-r ${showMismatch ? "border-red-200" : "border-gray-200"} px-2 py-2 text-sm text-center focus:outline-none`}
              placeholder="52"
              value={confirmCountryCode}
              onChange={(e) => handleConfirmCountryCode(e.target.value)}
              onPaste={(e) => e.preventDefault()}
            />
            <input
              id="phone-confirm-local"
              type="tel"
              autoComplete="off"
              className="flex-1 rounded-r-md px-3 py-2 text-sm focus:outline-none"
              placeholder="1234567890"
              value={confirmLocalNumber}
              onChange={(e) => handleConfirmLocalNumber(e.target.value)}
              onPaste={(e) => e.preventDefault()}
            />
          </div>
          {showMismatch && (
            <p className="text-xs text-red-500">Los números no coinciden.</p>
          )}
        </div>
      </div>
    </div>
  );
};
